import { displayAddress, isInspectionLocked, type V2Inspection } from '../domain/inspection';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_DRAFT_RETENTION_KEY } from '../persistence/persistenceContract';
import { putRecords } from '../persistence/v2Database';
import { createStore } from './createStore';
import { deleteInspectionAction } from './deleteInspection';
import { evidenceStore } from './evidenceStore';
import { findingStore } from './findingStore';
import { inspectionStore, prepareInspectionTouch } from './inspectionStore';
import { requestServerPurge } from './serverPurge';

// ABANDONED DRAFTS (Owner decision list 2026-10-19, retention rules): an inspection that never got its
// final report and has not been changed for 90 days is cleared — its photos, notes and findings on this
// device, and the server's AI review records of it. Two weeks before, the Inspections tab says so and offers
// 「保留」. The clock of every draft starts no earlier than the day this rule first ran for the account on
// this device, so a draft made before the rule existed still gets its full 90 days and its warning.
export const DRAFT_KEEP_DAYS = 90;
export const DRAFT_WARN_DAYS = 14;
const DAY_MS = 86_400_000;

interface RetentionRecord {
  since: number;
  cleared: { address: string; at: number }[];
}

export interface DraftNear {
  inspectionId: string;
  daysLeft: number;
}

export const draftRetentionStore = createStore<{ cleared: { address: string; at: number }[] }>({ cleared: [] });

function readRecord(): RetentionRecord | null {
  try {
    const key = scopedStorageKey(V2_DRAFT_RETENTION_KEY);
    if (key === null) return null;
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as Partial<RetentionRecord> | null;
    if (!parsed || typeof parsed.since !== 'number') return null;
    return { since: parsed.since, cleared: Array.isArray(parsed.cleared) ? parsed.cleared.filter((c) => typeof c?.address === 'string' && typeof c?.at === 'number') : [] };
  } catch {
    return null;
  }
}

function writeRecord(record: RetentionRecord): void {
  try {
    const key = scopedStorageKey(V2_DRAFT_RETENTION_KEY);
    if (key !== null) window.localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // a refused write only means the clock starts again next time — never earlier
  }
  draftRetentionStore.set(() => ({ cleared: record.cleared }));
}

// The last change to a draft: the inspection record, any of its areas, rows, findings or photos.
export function lastActivityOf(inspection: V2Inspection): number {
  let last = Math.max(inspection.updatedAt, inspection.createdAt);
  const state = inspectionStore.get();
  for (const area of state.areas) if (area.inspectionId === inspection.id) last = Math.max(last, area.updatedAt);
  for (const item of state.items) if (item.inspectionId === inspection.id) last = Math.max(last, item.updatedAt);
  for (const finding of findingStore.get().findings) if (finding.inspectionId === inspection.id) last = Math.max(last, finding.updatedAt);
  for (const evidence of evidenceStore.get().evidence) if (evidence.inspectionId === inspection.id) last = Math.max(last, evidence.updatedAt);
  return last;
}

function ageDays(inspection: V2Inspection, since: number, now: number): number {
  return (now - Math.max(lastActivityOf(inspection), since)) / DAY_MS;
}

function drafts(): V2Inspection[] {
  return inspectionStore.get().inspections.filter((i) => !isInspectionLocked(i));
}

// The drafts inside their last two weeks, soonest first. Nothing before the rule has started.
export function draftsNearCleanup(now: number = Date.now()): DraftNear[] {
  const record = readRecord();
  if (!record) return [];
  return drafts()
    .map((inspection) => ({ inspectionId: inspection.id, age: ageDays(inspection, record.since, now) }))
    .filter((d) => d.age >= DRAFT_KEEP_DAYS - DRAFT_WARN_DAYS && d.age < DRAFT_KEEP_DAYS)
    .map((d) => ({ inspectionId: d.inspectionId, daysLeft: Math.max(1, Math.ceil(DRAFT_KEEP_DAYS - d.age)) }))
    .sort((a, b) => a.daysLeft - b.daysLeft || a.inspectionId.localeCompare(b.inspectionId));
}

let running: Promise<number> | null = null;

// Starts the rule's clock for this account on its first run; afterwards clears every draft at 90 days.
// Returns how many drafts were cleared. Runs after an account's data is on screen (AppShell).
export function cleanupAbandonedDrafts(now: number = Date.now()): Promise<number> {
  if (running) return running;
  running = (async () => {
    const record = readRecord();
    if (!record) {
      writeRecord({ since: now, cleared: [] });
      return 0;
    }
    draftRetentionStore.set(() => ({ cleared: record.cleared }));
    const due = drafts().filter((inspection) => ageDays(inspection, record.since, now) >= DRAFT_KEEP_DAYS);
    const cleared = [...record.cleared];
    for (const inspection of due) {
      const address = displayAddress(inspection, '');
      await deleteInspectionAction(inspection.id);
      // A copy that was carried to another device by a file: its AI records are the other device's work.
      if (!inspection.movedOut) void requestServerPurge(inspection.id);
      cleared.push({ address, at: now });
    }
    if (due.length > 0) writeRecord({ since: record.since, cleared });
    return due.length;
  })();
  return running.finally(() => {
    running = null;
  });
}

// 「保留」: counts as a change to each draft, so its 90 days start again today.
export async function keepDraftsAction(inspectionIds: readonly string[], now: number = Date.now()): Promise<void> {
  const prepared = inspectionIds.map((id) => prepareInspectionTouch(id, now));
  const writes = prepared.flatMap((p) => (p.write ? [p.write] : []));
  if (writes.length > 0) await putRecords(writes);
  for (const p of prepared) p.apply();
}

export function dismissClearedDraftsNotice(): void {
  const record = readRecord();
  if (record) writeRecord({ since: record.since, cleared: [] });
  else draftRetentionStore.set(() => ({ cleared: [] }));
}

export function resetDraftRetentionForTests(): void {
  running = null;
  draftRetentionStore.set(() => ({ cleared: [] }));
}
